import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { territoriesApi, newsApi, partnersApi } from '../services/api';
import heroImage from '../assets/main44.png';

// ── Hooks ──────────────────────────────────────────────────────────────────
function useInView(threshold = 0.1) {
  const ref = useRef();
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

function Counter({ to, duration = 1400 }) {
  const [val, setVal] = useState(0);
  const [ref, vis] = useInView(0.3);
  useEffect(() => {
    if (!vis) return;
    let n = 0; const step = Math.max(1, Math.ceil(to / (duration / 16)));
    const t = setInterval(() => { n = Math.min(n + step, to); setVal(n); if (n >= to) clearInterval(t); }, 16);
    return () => clearInterval(t);
  }, [vis, to, duration]);
  return <span ref={ref}>{val}</span>;
}

// ── Ticker (territory names only) ─────────────────────────────────────────
function Ticker({ names }) {
  const items = names.length > 0 ? names : ['Vosges', 'Alsace', 'Lorraine'];
  return (
    <div style={{ overflow:'hidden', background:'var(--radar-deep)', padding:'9px 0' }}>
      <div style={{ display:'flex', animation:'ticker 22s linear infinite', whiteSpace:'nowrap' }}>
        {[...items, ...items, ...items].map((name, i) => (
          <span key={i} style={{ padding:'0 32px', fontFamily:'Space Mono,monospace', fontSize:11, color:'rgba(255,255,255,.9)', letterSpacing:'.14em', textTransform:'uppercase' }}>
            {name}<span style={{ color:'rgba(255,255,255,.3)', margin:'0 12px' }}>·</span>
          </span>
        ))}
      </div>
      <style>{`@keyframes ticker{from{transform:translateX(0)}to{transform:translateX(-33.33%)}}`}</style>
    </div>
  );
}

// ── Partners strip ─────────────────────────────────────────────────────────
function PartnersStrip({ partners }) {
  if (!partners.length) return null;
  return (
    <section style={{ padding:'clamp(24px,5vw,48px) var(--px,clamp(16px,5vw,40px))', background:'#fff', borderTop:'1px solid rgba(122,140,136,0.12)' }}>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <p className="section-label" style={{ marginBottom:24, textAlign:'center' }}>Ils nous font confiance</p>
        <div style={{ display:'flex', gap:32, alignItems:'center', justifyContent:'center', flexWrap:'wrap' }}>
          {partners.map(p => (
            <a key={p._id} href={p.website || '#'} target="_blank" rel="noopener noreferrer"
              style={{ display:'flex', alignItems:'center', justifyContent:'center', opacity:0.75, transition:'opacity .2s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.75'}>
              {p.logoUrl
                ? <img src={p.logoUrl.startsWith('/uploads') ? `${process.env.REACT_APP_API_BASE || ''}${p.logoUrl}` : p.logoUrl} alt={p.name} style={{ maxHeight:52, maxWidth:140, objectFit:'contain' }} />
                : <div style={{ height:44, padding:'0 20px', background:'var(--radar-accent3, #FFF3E0)', borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Space Mono,monospace', fontSize:12, color:'var(--radar-deep)', fontWeight:700 }}>{p.name}</div>
              }
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── News card ──────────────────────────────────────────────────────────────
function NewsCard({ article, index }) {
  const navigate = useNavigate();
  const [ref, vis] = useInView();
  const [hov, setHov] = useState(false);
  return (
    <article ref={ref} role="button" tabIndex={0} aria-label={`Lire : ${article.title}`}
      onClick={() => navigate(`/actualites/${article._id}`)}
      onKeyDown={e => e.key === 'Enter' && navigate(`/actualites/${article._id}`)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        opacity: vis ? 1 : 0, transform: vis ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.55s ease ${index * 100}ms`,
        background:'#fff', border:`1px solid ${hov ? 'var(--radar-orange)' : 'rgba(122,140,136,0.18)'}`,
        borderRadius:8, overflow:'hidden', cursor:'pointer',
        boxShadow: hov ? '0 14px 36px rgba(217,79,0,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
        outline:'none',
      }}>
      <div style={{ height:180, overflow:'hidden', background:'linear-gradient(135deg,#0F1E1B,#1B3832)', flexShrink:0 }}>
        {article.coverImage
          ? <img src={article.coverImage} alt={article.title} style={{ width:'100%', height:'100%', objectFit:'cover', transform:hov?'scale(1.04)':'scale(1)', transition:'transform .4s ease' }} />
          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontSize:40, opacity:.2 }}>📰</span></div>}
      </div>
      <div style={{ padding:'18px 20px 20px' }}>
        {article.isPinned && <span style={{ display:'inline-block', background:'var(--radar-accent3, #FFF3E0)', color:'var(--radar-orange)', fontSize:9, fontFamily:'Space Mono,monospace', letterSpacing:'.1em', padding:'2px 7px', borderRadius:2, marginBottom:8, textTransform:'uppercase' }}>📌 Épinglé</span>}
        <h3 style={{ fontSize:15, fontWeight:700, lineHeight:1.35, marginBottom:7, color:'var(--radar-dark)' }}>{article.title}</h3>
        {article.excerpt && <p style={{ fontSize:13, color:'#5A6B68', lineHeight:1.6, marginBottom:12 }}>{article.excerpt.substring(0,100)}{article.excerpt.length>100?'…':''}</p>}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <time style={{ fontSize:11, color:'var(--radar-mid)', fontFamily:'Space Mono,monospace' }}>
            {new Date(article.createdAt).toLocaleDateString('fr-FR',{day:'numeric',month:'short',year:'numeric'})}
          </time>
          <span style={{ fontSize:11, color:'var(--radar-deep)', fontFamily:'Space Mono,monospace', fontWeight:600, opacity:hov?1:.55, transition:'opacity .2s' }}>Lire →</span>
        </div>
      </div>
    </article>
  );
}

// ── Territory card ─────────────────────────────────────────────────────────
function TerritoryCard({ t, index }) {
  const [ref, vis] = useInView();
  const [hov, setHov] = useState(false);
  return (
    <Link to={`/territoire?t=${t.id}`} style={{ textDecoration:'none' }}>
      <div ref={ref}
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{
          opacity: vis ? 1 : 0,
          background:'#fff', border:`1px solid ${hov ? t.color : 'rgba(122,140,136,0.2)'}`,
          borderRadius:8, cursor:'pointer',
          boxShadow: hov ? `0 16px 40px rgba(0,0,0,0.1)` : '0 2px 8px rgba(0,0,0,0.04)',
          transform: hov ? 'translateY(-5px)' : (vis ? 'translateY(0)' : 'translateY(24px)'),
          transition: `all 0.35s ease ${index * 90}ms`,
        }}>
        <div style={{ height:5, background:t.color }} />
        {t.avatarUrl && (
          <div style={{ padding:'0 22px', marginTop:-20, display:'flex', alignItems:'center', gap:12, position:'relative', zIndex:2 }}>
            <div style={{ width:52, height:52, borderRadius:'50%', overflow:'hidden', border:`3px solid ${t.color}`, background:'#fff', flexShrink:0, boxShadow:'0 3px 14px rgba(0,0,0,0.18)' }}>
              <img src={t.avatarUrl.startsWith('/uploads') ? `${process.env.REACT_APP_API_BASE || ''}${t.avatarUrl}` : t.avatarUrl} alt={t.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            </div>
          </div>
        )}
        <div style={{ padding: t.avatarUrl ? '10px 22px 20px' : '20px 22px' }}>
          <h3 style={{ fontSize:19, fontWeight:700, marginBottom:3 }}>{t.name}</h3>
          <p style={{ fontSize:11, color:'var(--radar-mid)', fontFamily:'Space Mono,monospace', marginBottom:10 }}>
            {t.department ? `${t.department} · ` : ''}{t.region}
          </p>
          {t.description && <p style={{ fontSize:12, color:'#5A6B68', lineHeight:1.55, marginBottom:14 }}>{t.description.substring(0,80)}{t.description.length>80?'…':''}</p>}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(220px,100%),1fr))', gap:4, padding:'10px 0', borderTop:'1px solid rgba(122,140,136,0.1)', borderBottom:'1px solid rgba(122,140,136,0.1)', marginBottom:12 }}>
            {[{v:t.players||0,l:'Joueurs'},{v:t.teams||0,l:'Équipes'},{v:t.nextEvent?new Date(t.nextEvent).toLocaleDateString('fr-FR',{day:'numeric',month:'short'}):'—',l:'Prochain',c:t.color}].map((s,i)=>(
              <div key={i} style={{ textAlign:'center' }}>
                <div style={{ fontSize:i<2?16:12, fontWeight:700, color:s.c||'var(--radar-dark)' }}>{s.v}</div>
                <div style={{ fontSize:9, color:'var(--radar-mid)', textTransform:'uppercase', letterSpacing:'.08em' }}>{s.l}</div>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display:'flex', gap:4 }}>
              {['Carte','Classement','Galerie'].map(l=>(
                <span key={l} style={{ padding:'2px 7px', background:'var(--radar-accent3, #FFF3E0)', borderRadius:2, fontSize:9, fontFamily:'Space Mono,monospace', color:'var(--radar-deep)' }}>{l}</span>
              ))}
            </div>
            <span style={{ fontSize:11, color:t.color, fontFamily:'Space Mono,monospace', fontWeight:600, opacity:hov?1:0, transition:'opacity .2s' }}>Voir →</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [territories, setTerritories] = useState([]);
  const [news, setNews] = useState([]);
  const [partners, setPartners] = useState([]);
  const [heroVis, setHeroVis] = useState(false);
  const [offsetY, setOffsetY] = useState(0);

  // Scroll listener to update the offset
  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch API Data
  useEffect(() => {
    territoriesApi.list().then(setTerritories).catch(console.error);
    newsApi.list({ limit: 6 }).then(setNews).catch(console.error);
    partnersApi.listGlobal().then(setPartners).catch(console.error);
    const t = setTimeout(() => setHeroVis(true), 80);
    return () => clearTimeout(t);
  }, []);

  const values = [
    { icon:'👁️', title:'Observer', desc:'Lire le territoire, comprendre l\'espace, développer une conscience du lieu.' },
    { icon:'🧠', title:'Analyser', desc:'Combiner intelligence collective, orientation et stratégie d\'équipe.' },
    { icon:'🤝', title:'Agir ensemble', desc:'La coopération comme moteur — chaque équipier compte.' },
    { icon:'👍', title:'Réussir', desc:'Célébrer chaque checkpoint, chaque effort partagé, chaque découverte.' },
  ];

  return (
    <div style={{ paddingTop:'var(--nav-h, 64px)', overflow:'hidden' }}>
      <style>{`
        @keyframes pulse-blob{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}
        @keyframes ring-rot{from{transform:translateY(-50%) rotate(0)}to{transform:translateY(-50%) rotate(360deg)}}
        @keyframes float-dot{from{transform:translateY(0)}to{transform:translateY(-13px)}}
        
        /* New responsive parallax image styles */
        .hero-parallax-img {
          position: absolute;
          top: 3%;
          right: 5%;
          width: 450px;
          pointer-events: none;
          z-index: 0;
          /* Color/Overlay treatment to ensure text readability */
          opacity: 0.6;
          mix-blend-mode: multiply; /* Blends the image with the background gradient */
          filter: contrast(0.8) brightness(1.1);
          transition: transform 0.1s ease-out;
        }

        /* Text container background to guarantee readability */
        .hero-text-container {
          max-width: 780px;
          position: relative;
          z-index: 1;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .hero-parallax-img {
            width: 150%; /* Scales the image up to bleed off the edges */
            right: -25%; /* Centers it more behind the text */
            top: 15%;
            opacity: 0.15; /* Drastically lower opacity on mobile to act as a watermark */
          }
          .hero-text-container {
            /* Adds a soft glowing gradient behind the text on mobile */
            background: radial-gradient(circle, rgba(244,241,236,0.85) 0%, rgba(244,241,236,0) 70%);
            padding: 20px 0;
          }
        }
      `}</style>

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ minHeight:'90vh', display:'flex', flexDirection:'column', justifyContent:'center', padding:'80px 40px 40px', position:'relative', overflow:'hidden', background:'linear-gradient(160deg, #F4F1EC 55%, #E8F4F1 100%)' }}>
        
        {/* RESPONSIVE PARALLAX IMAGE */}
        <img 
          src={heroImage}
          alt="Floating visual"
          className="hero-parallax-img"
          style={{ transform: `translateY(${offsetY * 0.4}px)` }}
        />

        {/* TEXT CONTAINER WITH Z-INDEX AND MOBILE GLOW */}
        <div className="hero-text-container">
          <div style={{ opacity:heroVis?1:0, transform:heroVis?'none':'translateX(-20px)', transition:'all .8s ease .1s' }}>
            <p className="section-label" style={{ marginBottom:18 }}>Jeu de piste</p>
          </div>
          <div style={{ opacity:heroVis?1:0, transform:heroVis?'none':'translateY(18px)', transition:'all .9s ease .3s' }}>
            <h1 style={{ fontSize:'clamp(48px,7.5vw,88px)', fontWeight:700, lineHeight:1.0, letterSpacing:'-.03em', marginBottom:24, color:'var(--radar-dark)' }}>
              Bienvenue<br />sur{' '}
              <span style={{ color:'var(--radar-deep)', position:'relative', display:'inline-block' }}>
                RADAR
                <span style={{ position:'absolute', bottom:-2, left:0, right:0, height:4, background:'var(--radar-orange)', opacity:.22, borderRadius:2, transform:heroVis?'scaleX(1)':'scaleX(0)', transformOrigin:'left', transition:'transform 1.3s ease 1s' }} />
              </span>
            </h1>
          </div>
          <div style={{ opacity:heroVis?1:0, transform:heroVis?'none':'translateY(16px)', transition:'all .9s ease .5s' }}>
            <p style={{ fontSize:18, color:'#3D5550', maxWidth:500, lineHeight:1.65, marginBottom:40 }}>
              Un jeu de piste qui permet de découvrir un territoire
            </p>
          </div>
          <div style={{ opacity:heroVis?1:0, transition:'all .9s ease .7s', display:'flex', gap:14, flexWrap:'wrap' }}>
            <Link to="/territoire" className="btn-primary">Les territoires →</Link>
            <Link to="/LancerVotreRadar" style={{ display:'inline-block', padding:'11px 28px', border:'1.5px solid var(--radar-dark)', color:'var(--radar-dark)', fontFamily:'Space Mono,monospace', fontSize:12, letterSpacing:'.05em', textTransform:'uppercase', borderRadius:4, transition:'all .2s', background:'transparent' }}
              onMouseEnter={e=>{e.currentTarget.style.background='var(--radar-dark)';e.currentTarget.style.color='var(--radar-cream)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='var(--radar-dark)';}}>
              Lancer votre RADAR
            </Link>
          </div>
        </div>
      </section>

      {/* Ticker — territory names only */}
      <Ticker names={territories.map(t => t.name)} />

      {/* ── PICTOGRAMS ─────────────────────────────────────────────────── */}
      <section style={{ background:'var(--radar-dark)', padding:'0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(200px,100%),1fr))' }}>
          {values.map(({ icon, title, desc }, i) => (
            <div key={i} style={{ padding:'44px 28px', textAlign:'center', borderRight:i<3?'1px solid rgba(255,255,255,.06)':'none', transition:'background .25s', cursor:'default' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(217,79,0,.15)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
                <div style={{ width:54, height:54, borderRadius:'50%', background:'rgba(217,79,0,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize: 26 }}>
                  {icon}
                </div>
              </div>
              <div style={{ fontFamily:'Space Mono,monospace', fontSize:13, fontWeight:700, color:'#fff', marginBottom:8, letterSpacing:'.04em' }}>{title}</div>
              <p style={{ fontSize:12, color:'rgba(255,255,255,.45)', lineHeight:1.6 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ──────────────────────────────────────────────────────── */}
      <section style={{ background:'var(--radar-deep)', padding:'0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(200px,100%),1fr))' }}>
          {[
            {v:territories.reduce((s,t)=>s+(t.players||0),0)||304, l:'Participants', suf:'+'},
            {v:territories.reduce((s,t)=>s+(t.teams||0),0)||38,   l:'Équipes', suf:''},
            {v:territories.length||3, l:'Territoires', suf:''},
            {v:100, l:'Énergie', suf:'%'},
          ].map((s,i) => (
            <div key={i} style={{ textAlign:'center', padding:'32px 16px', borderRight:i<3?'1px solid rgba(255,255,255,.15)':'none' }}>
              <div style={{ fontSize:38, fontWeight:700, color:'#fff', fontFamily:'Space Mono,monospace', lineHeight:1, marginBottom:6 }}>
                <Counter to={s.v} />{s.suf}
              </div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.6)', textTransform:'uppercase', letterSpacing:'.1em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Partners (shown right after stats if any) */}
      <PartnersStrip partners={partners} />

      {/* ── TERRITORIES ────────────────────────────────────────────────── */}
      <section style={{ padding:'clamp(40px,7vw,80px) var(--px,clamp(16px,5vw,40px))', background:'#fff' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:44, flexWrap:'wrap', gap:12 }}>
            <div>
              <p className="section-label" style={{ marginBottom:10 }}>Les territoires actifs</p>
              <h2 style={{ fontSize:'clamp(22px,4vw,34px)', fontWeight:700, letterSpacing:'-.02em' }}>
                {territories.length||3} territoire{territories.length!==1?'s':''} vous attendent
              </h2>
            </div>
            <Link to="/territoire" style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-deep)', letterSpacing:'.05em', textTransform:'uppercase' }}>Voir tous →</Link>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:20 }}>
            {territories.map((t,i) => <TerritoryCard key={t.id} t={t} index={i} />)}
          </div>
        </div>
      </section>

      {/* ── NEWS ───────────────────────────────────────────────────────── */}
      {news.length > 0 && (
        <section style={{ padding:'clamp(40px,7vw,80px) var(--px,clamp(16px,5vw,40px))', background:'var(--radar-cream)' }}>
          <div style={{ maxWidth:1100, margin:'0 auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:44, flexWrap:'wrap', gap:12 }}>
              <div>
                <p className="section-label" style={{ marginBottom:10 }}>Actualités</p>
                <h2 style={{ fontSize:'clamp(22px,4vw,34px)', fontWeight:700, letterSpacing:'-.02em' }}>Dernières nouvelles</h2>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:20 }}>
              {news.map((a,i) => <NewsCard key={a._id} article={a} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <section style={{ padding:'clamp(40px,7vw,80px) var(--px,clamp(16px,5vw,40px))', background:'var(--radar-deep)', color:'#fff', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:'-15%', right:'-3%', width:340, height:340, borderRadius:'50%', background:'rgba(255,255,255,.05)', pointerEvents:'none' }} />
        <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:32, position:'relative' }}>
          <div>
            <h2 style={{ fontSize:'clamp(24px,3.5vw,38px)', fontWeight:700, marginBottom:12, letterSpacing:'-.02em' }}>
              Lancez votre RADAR dans votre territoire
            </h2>
            <p style={{ fontSize:16, opacity:.8, maxWidth:440 }}>Rejoignez le réseau. Accompagnement complet de A à Z.</p>
          </div>
          <Link to="/LancerVotreRadar" style={{ background:'#fff', color:'var(--radar-deep)', padding:'14px 30px', fontFamily:'Space Mono,monospace', fontSize:12, letterSpacing:'.05em', textTransform:'uppercase', borderRadius:4, whiteSpace:'nowrap', display:'inline-block', transition:'transform .2s, box-shadow .2s' }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 10px 28px rgba(0,0,0,.15)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
            En savoir plus →
          </Link>
        </div>
      </section>

      <footer style={{ padding:'28px 40px', borderTop:'1px solid rgba(122,140,136,.15)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
        <span style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-mid)' }}>© 2025 RADAR - Tous droits réservés.</span>
        <div style={{ display:'flex', gap:20 }}>
          <a href="https://www.instagram.com/radar_morlaix_co/" target="_blank" rel="noopener noreferrer" style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-mid)' }}>Instagram</a>
          <Link to="/qui-sommes-nous" style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-mid)' }}>À propos</Link>
          <Link to="/mentions-legales" style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-mid)' }}>Mentions légales</Link>
          <Link to="/confidentialite" style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-mid)' }}>Confidentialité</Link>
          <Link to="/cookies" style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-mid)' }}>Cookies</Link>
          <Link to="/login" style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-mid)', opacity:.35 }}>Admin</Link>
        </div>
      </footer>
    </div>
  );
}